import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Bell, CheckCheck, Wallet, Info, Tag, AlertCircle } from "lucide-react";
import {
  getNotifications,
  markNotificationRead,
  markAllNotificationsRead,
} from "../api/apiServices";

// ── Helpers ───────────────────────────────────────────────────────────────────

const TYPE_CONFIG = {
  TRANSACTION: { icon: Wallet,      bg: "bg-blue-100",   text: "text-blue-600"   },
  PROMO:       { icon: Tag,         bg: "bg-purple-100", text: "text-purple-600" },
  ALERT:       { icon: AlertCircle, bg: "bg-red-100",    text: "text-red-500"    },
  DEFAULT:     { icon: Info,        bg: "bg-gray-100",   text: "text-gray-500"   },
};

function typeConfig(type) {
  return TYPE_CONFIG[type] ?? TYPE_CONFIG.DEFAULT;
}

function timeAgo(dateStr) {
  const diff = (Date.now() - new Date(dateStr)) / 1000;
  if (diff < 60)    return "just now";
  if (diff < 3600)  return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function NotificationsPage() {
  const navigate = useNavigate();

  const [items, setItems]         = useState([]);
  const [unread, setUnread]       = useState(0);
  const [page, setPage]           = useState(1);
  const [total, setTotal]         = useState(0);
  const [loading, setLoading]     = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [markingAll, setMarkingAll]   = useState(false);
  const [error, setError]         = useState("");

  const LIMIT = 20;
  const hasMore = items.length < total;

  // ── Fetch ────────────────────────────────────────────────────────────────

  const fetchPage = useCallback(async (pageNum, replace = false) => {
    try {
      replace ? setLoading(true) : setLoadingMore(true);
      const res = await getNotifications(pageNum, LIMIT);
      const data = res.data;

      setTotal(data.total);
      setUnread(data.unread);
      setItems((prev) => replace ? data.items : [...prev, ...data.items]);
      setPage(pageNum);
    } catch {
      setError("Failed to load notifications.");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => { fetchPage(1, true); }, [fetchPage]);

  // ── Mark one as read ─────────────────────────────────────────────────────

  async function handleRead(notification) {
    // Navigate immediately if there's a click action
    const dest = notification.metadata?.click_action;

    if (!notification.isRead) {
      // Optimistic update
      setItems((prev) =>
        prev.map((n) =>
          n._id === notification._id ? { ...n, isRead: true } : n
        )
      );
      setUnread((u) => Math.max(0, u - 1));

      try {
        await markNotificationRead(notification._id);
      } catch {
        // Roll back if it fails
        setItems((prev) =>
          prev.map((n) =>
            n._id === notification._id ? { ...n, isRead: false } : n
          )
        );
        setUnread((u) => u + 1);
      }
    }

    if (dest) navigate(dest);
  }

  // ── Mark all as read ─────────────────────────────────────────────────────

  async function handleMarkAll() {
    if (unread === 0 || markingAll) return;
    setMarkingAll(true);

    const prev = items;
    const prevUnread = unread;

    setItems((all) => all.map((n) => ({ ...n, isRead: true })));
    setUnread(0);

    try {
      await markAllNotificationsRead();
    } catch {
      setItems(prev);
      setUnread(prevUnread);
      setError("Failed to mark all as read.");
    } finally {
      setMarkingAll(false);
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white px-5 pt-6 pb-5 flex items-center justify-between shadow-sm sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)}>
            <ArrowLeft size={22} className="text-gray-700" />
          </button>
          <h1 className="text-[17px] font-semibold text-gray-800">
            Notifications
            {unread > 0 && (
              <span className="ml-2 text-xs font-bold bg-red-500 text-white rounded-full px-2 py-0.5">
                {unread}
              </span>
            )}
          </h1>
        </div>

        {unread > 0 && (
          <button
            onClick={handleMarkAll}
            disabled={markingAll}
            className="flex items-center gap-1.5 text-sm font-medium text-[#3866A3] disabled:opacity-50"
          >
            <CheckCheck size={16} />
            {markingAll ? "Marking..." : "Mark all read"}
          </button>
        )}
      </div>

      {/* Body */}
      <div className="max-w-md mx-auto pb-10">
        {loading ? (
          <div className="space-y-3 p-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-white rounded-2xl p-4 flex gap-3 animate-pulse">
                <div className="w-10 h-10 rounded-full bg-gray-200 flex-shrink-0" />
                <div className="flex-1 space-y-2 py-1">
                  <div className="h-3 bg-gray-200 rounded w-3/4" />
                  <div className="h-3 bg-gray-200 rounded w-full" />
                  <div className="h-2 bg-gray-100 rounded w-1/4" />
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="p-8 text-center text-red-500 text-sm">{error}</div>
        ) : items.length === 0 ? (
          <EmptyState />
        ) : (
          <AnimatePresence initial={false}>
            <div className="p-4 space-y-3">
              {items.map((n) => (
                <NotificationItem key={n._id} notification={n} onRead={handleRead} />
              ))}
            </div>
          </AnimatePresence>
        )}

        {/* Load more */}
        {hasMore && !loading && (
          <div className="flex justify-center py-4">
            <button
              onClick={() => fetchPage(page + 1)}
              disabled={loadingMore}
              className="text-sm font-medium text-[#3866A3] disabled:opacity-50"
            >
              {loadingMore ? "Loading..." : "Load more"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function NotificationItem({ notification: n, onRead }) {
  const { icon: Icon, bg, text } = typeConfig(n.type);

  return (
    <motion.button
      layout
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.97 }}
      onClick={() => onRead(n)}
      className={`w-full text-left bg-white rounded-2xl p-4 flex gap-3 shadow-sm transition-all border ${
        n.isRead ? "border-transparent" : "border-[#3866A3]/20"
      }`}
    >
      {/* Icon */}
      <div className={`w-10 h-10 rounded-full ${bg} flex-shrink-0 flex items-center justify-center`}>
        <Icon size={18} className={text} />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <p className={`text-sm leading-snug ${n.isRead ? "font-medium text-gray-700" : "font-semibold text-gray-900"}`}>
            {n.title}
          </p>
          {!n.isRead && (
            <span className="w-2 h-2 rounded-full bg-[#3866A3] flex-shrink-0 mt-1.5" />
          )}
        </div>
        <p className="text-xs text-gray-500 mt-0.5 leading-relaxed line-clamp-2">{n.body}</p>
        <p className="text-[10px] text-gray-400 mt-1.5">{timeAgo(n.createdAt)}</p>
      </div>
    </motion.button>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-24 px-8 text-center">
      <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
        <Bell size={28} className="text-gray-400" />
      </div>
      <p className="text-gray-700 font-semibold">You're all caught up</p>
      <p className="text-gray-400 text-sm mt-1">No notifications yet</p>
    </div>
  );
}